import { ZoomRenderer } from '@ikegps/core-shared';
import { ResizeHandle } from '../../components/resizable';
import { Spinner } from '../../components/spinner/spinner';
import { T, Translate } from '../../i18n/index';
import { type CustomMithrilVNode, m, type MithrilVNode } from '../../mithril';
import { CurrentState } from '../../state/page.state';
import { ElementSizeTracker } from '../../util/element.size';
import { CollectionMetaInfo } from './collection.meta.info/collection.meta.info';
import {
	CSS,
	FIELDS_MIN_HEIGHT,
	ZOOM_HEIGHT,
	ZOOM_MIN_HEIGHT,
	ZOOM_PANEL_VAR,
	ZOOM_WIDTH,
} from './collection.view.style';
import { CollectionEditViewSubform } from './collection.view.subform';
import { CollectionEditViewTitle } from './collection.view.title';
import type { EditableCollectionController } from './collection.view.ctrl.interface';

export interface CollectionViewOptions {
	isEditable: boolean;
}
export class CollectionEdit {
	zoomRenderer: ZoomRenderer;

	private zoomSize: ElementSizeTracker;

	private zoomHandle = new ResizeHandle({
		axis: 'y',
		cssVar: ZOOM_PANEL_VAR,
		target: 'previous',
		min: ZOOM_MIN_HEIGHT,
		minAdjacent: FIELDS_MIN_HEIGHT,
		storageKey: 'panel.size/measure/zoom',
	});

	constructor(public ctrl: EditableCollectionController) {
		this.zoomRenderer = new ZoomRenderer({
			zoomLevel: 2,
			size: {
				width: parseInt(ZOOM_WIDTH),
				height: parseInt(ZOOM_HEIGHT),
			},
			useCrosshair: true,
			crosshairCenterGap: 7.5,
		});
		this.zoomSize = new ElementSizeTracker((width, height) => this.zoomRenderer.setSize(width, height));
	}

	view(toggleableElement: MithrilVNode = null) {
		if (!this.ctrl.isCollectionSelected) {
			return null;
		}

		const collection = this.ctrl.collection;
		if (collection == null) {
			if (!this.ctrl.isCollectionLoading) {
				return m('div', { className: CSS.container + ' ' + CSS.isMissing }, Translate(T.COLLECTION_MISSING));
			}

			return m('div', { className: CSS.container + ' ' + CSS.isLoading }, m('span', Spinner.view()));
		}

		const isZoomShown = this.ctrl.isPhotomeasure && this.ctrl.isZoomShown;

		const containerElements = [
			CollectionEditViewTitle.view(this.ctrl),
			this.ctrl.isCollectionVariant ? m('div', { className: CSS.variantBanner }, Translate(T.VARIANT_READ_ONLY)) : null,
			this.ctrl.isMetadataShown ? CollectionMetaInfo.view(collection) : null,
			isZoomShown ? this.viewZoom() : null,
			isZoomShown ? this.zoomHandle.view() : null,
			CollectionEditViewSubform.view(this.ctrl, collection),
		];

		if (toggleableElement) {
			containerElements.push(toggleableElement);
		}

		return m('div', { className: CSS.container }, containerElements);
	}

	private viewZoom() {
		const zoomNode = this.zoomRenderer.view(CurrentState.CollectionEdit.currentDraw);
		return m(
			'div',
			{
				className: CSS.zoom,
				oncreate: (vnode: CustomMithrilVNode) => this.zoomSize.track(vnode.dom),
				onupdate: (vnode: CustomMithrilVNode) => this.zoomSize.track(vnode.dom),
				onremove: () => this.zoomSize.stop(),
			},
			zoomNode != null ? zoomNode : Translate(T.SELECT_MEASUREMENT)
		);
	}
}
